<template>
  <BbPopover
    v-model="showMenu"
    :hideCloseButton="true"
    location="bottom"
    contentClass="supported-languages"
    minWidth="300px"
    minHeight="400px"
  >
    <template #default>
      <BbBtn class="create-button" size="small" type="secondary">
        <template #prepend>
          <BbIcon size="small" name="plusincircle" />
        </template>
        <TextBody i18n-key="translation.newLanguage" />
      </BbBtn>
    </template>
    <template #popover>
      <div class="popover-container">
        <div class="search-container">
          <BbInput
            v-model="filterValue"
            class="search-input"
            size="small"
            :placeholder="t('common.search')"
            v-if="showMenu"
            autofocus
          >
            <template #prepend-inner>
              <BbIcon size="small" name="search" />
            </template>
          </BbInput>
        </div>
        <div ref="languagesContainer" class="languages-container">
          <div class="language-name custom-language" @click="openCustomPopup">
            <BbIcon class="custom-icon" size="small" name="plus-small" />
            <span>{{ t('glossaries.customLanguage.custom') }}</span>
          </div>
          <div
            v-for="item in filteredLanguages"
            :key="item.language"
            class="language-name"
            @click="addLanguageColumn(item)"
          >
            {{ item.name }}
          </div>
        </div>
      </div>
    </template>
  </BbPopover>
</template>

<script>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import TextBody from '~/components/common/TextBody';

export default {
  components: { TextBody },
  emits: ['change-language', 'create-custom'],
  props: {
    supportedLanguages: {
      type: Array,
      default: () => [],
    },
  },

  setup(props, { emit }) {
    const { t } = useI18n();
    const showMenu = ref(false);
    const selectedLanguage = ref('');
    const filterValue = ref('');
    const languagesContainer = ref(null);

    // the popover keeps its content between openings, so the list would reopen where it was last scrolled
    watch(showMenu, async (isOpened) => {
      if (!isOpened) return;
      await nextTick();
      if (languagesContainer.value) languagesContainer.value.scrollTop = 0;
    });

    const filteredLanguages = computed(() =>
      props.supportedLanguages.filter((lang) => lang.name.toLowerCase().includes(filterValue.value.toLowerCase())),
    );

    const addLanguageColumn = (event) => {
      emit('change-language', event);
      selectedLanguage.value = '';
      showMenu.value = false;
      filterValue.value = '';
    };

    const openCustomPopup = () => {
      emit('create-custom');
      showMenu.value = false;
      filterValue.value = '';
    };

    return {
      addLanguageColumn,
      openCustomPopup,
      filteredLanguages,
      filterValue,
      languagesContainer,
      selectedLanguage,
      showMenu,
      t,
    };
  },
};
</script>

<style scoped lang="scss">
.popover-container {
  display: flex;
  flex-direction: column;
  // fills the popover's 400px min height minus its 12px vertical padding, so only the list scrolls and the
  // search field stays put with its padding instead of the whole popover scrolling underneath it
  height: 376px;

  .search-container {
    flex-shrink: 0;
  }

  .search-input {
    background: $bb-neutral-0;
  }

  .languages-container {
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-3;
    padding: $bb-spacing-3 $bb-spacing-4 $bb-spacing-3 $bb-spacing-4;
    overflow-y: auto;
    flex: 1;
    min-height: 0;

    .language-name {
      @include bb-regular-text-styles('lg');
      cursor: pointer;

      &:hover {
        color: $bb-primary-3;
        background-color: $bb-neutral-0;
      }

      // text stays aligned with the language names, icon hangs in the left padding
      &.custom-language {
        position: relative;
        display: flex;
        align-items: center;

        .custom-icon {
          position: absolute;
          left: -$bb-spacing-4;
        }
      }
    }
  }
}
</style>
